import { Button, Logo, ringMarkUrl } from "@orkestor/design-system";
import { useAuth } from "@orkestor/core";

import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/** Screen 01 — la portada pública. Marca a la izquierda, acceso a la derecha.
    «Entrar» abre Keycloak en popup (hoy stub); «Crear cuenta» irá al registro
    del realm. El botón de Google se omite: no hay proveedor configurado. */
export interface PortadaProps {
  /** Where an already-authenticated visitor is sent. The shell decides this:
      the platform skin must not know a product's routes, or the two skins are
      coupled and moving Pencil Chief would break the landing page. */
  homePath: string;
}

export function Portada({ homePath }: PortadaProps) {
  const { status, signIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === "authenticated") navigate(homePath, { replace: true });
  }, [status, navigate, homePath]);

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      {/* Marca */}
      <section className="relative flex flex-col justify-center overflow-hidden bg-acc-ink px-10 py-16 md:px-16">
        {/* Marca de agua: el trazo de la anilla (mockup) */}
        <img
          src={ringMarkUrl}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-36 -right-40 w-[520px] max-w-none select-none opacity-[0.09]"
        />
        <div className="relative flex flex-col">
          {/* Altura en progresión geométrica: wordmark/H1 = H1/subtítulo = 31/13.5 → ~71px */}
          <Logo className="mb-8 h-[71px] self-start" />
        <h1 className="max-w-[15ch] text-[31px] font-bold leading-[1.18] tracking-tight">
          Todo el equipo de tu rodaje, en <em className="not-italic text-acc">un solo sitio</em>.
        </h1>
        <p className="mt-3.5 max-w-[36ch] text-[13.5px] leading-relaxed text-dim">
          Audiovisual Resource Management: las herramientas de producción del
          sector, sobre una misma cuenta y una misma agenda.
        </p>
        <ul className="mt-6 flex list-none flex-col gap-2.5 p-0 text-[13px] text-dim">
          <li className="flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-acc" /> Pencil Chief — encuentra técnicos
          </li>
          <li className="flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-mute" /> Check Me — control de tiempo
          </li>
          <li className="flex items-center gap-2.5">
            <span className="h-1.5 w-1.5 rounded-full bg-mute" /> Why Humans — casting de artistas
          </li>
        </ul>
        </div>
      </section>

      {/* Acceso */}
      <section className="flex items-center justify-center border-l border-line bg-bg px-6 py-16">
        <div className="flex w-[340px] max-w-full flex-col gap-3.5">
          <h2 className="text-[21px] font-bold">Entra en Orkestor</h2>
          <p className="text-[13px] leading-relaxed text-dim">
            Una cuenta para los tres productos.
          </p>
          <Button onClick={signIn} className="w-full">
            Entrar
          </Button>
          <Button variant="secondary" className="w-full" disabled>
            Crear cuenta
          </Button>
          <p className="text-center text-[10.5px] leading-relaxed text-mute">
            Al continuar aceptas los{" "}
            <a
              href="https://orkestor.com/legal/terminos.html"
              target="_blank"
              rel="noreferrer"
              className="text-dim underline hover:text-acc"
            >
              términos del servicio
            </a>
            .
          </p>
          <p className="mt-2 border-t border-line pt-3 text-center font-mono text-[10.5px] leading-relaxed text-mute">
            Identidad gestionada por Keycloak · realm{" "}
            <span className="text-dim">orkestor</span>
          </p>
        </div>
      </section>
    </div>
  );
}
